import { Transformer } from '@lexical/markdown';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $isTableNode } from '@lexical/table';
import { $getRoot, LexicalEditor, SKIP_SCROLL_INTO_VIEW_TAG } from 'lexical';
import { useEffect, useRef } from 'react';
import { MarkdownImage } from 'src/company/templates/hooks/useConvertToMarkdownMutation';
import { createTransformers } from '../config/transformers';
import {
  $getEditorValue,
  $setEditorValue,
  replaceImagesWithPlaceholders,
  stripNoteSuggestionTags,
} from '../utils';
import { captureScrollPosition } from '../utils/captureScrollPosition';
import { $acceptSuggestions, $discardSuggestions } from '../utils/suggestion-pipeline';
import { isHtmlOrigin, markAsHtmlOrigin } from '../utils/tableOrigin';

export type EditorHelpers = {
  editor: LexicalEditor;
  getContent: () => string;
  getRawContent: () => string;
  setContent: (markdown: string, onComplete?: () => void) => void;
  applyInlineSuggestions: () => void;
  discardSuggestions: () => void;
  setEditable: (editable: boolean) => void;
};

type EditorReadyPluginProps = {
  onEditorReady?: (helpers: EditorHelpers) => void;
  placeholderImages: MarkdownImage[];
};

function $collectHtmlOriginTableIndices(): Set<number> {
  const indices = new Set<number>();
  let tableIdx = 0;
  for (const child of $getRoot().getChildren()) {
    if ($isTableNode(child)) {
      if (isHtmlOrigin(child)) indices.add(tableIdx);
      tableIdx++;
    }
  }
  return indices;
}

function $restoreHtmlOriginMarkers(indices: Set<number>): void {
  if (indices.size === 0) return;
  let tableIdx = 0;
  for (const child of $getRoot().getChildren()) {
    if ($isTableNode(child)) {
      if (indices.has(tableIdx)) markAsHtmlOrigin(child);
      tableIdx++;
    }
  }
}

function $cleanOrphanHeadingsAndRenumber(transformers: Transformer[]): void {
  const original = $getEditorValue(transformers);
  const cleaned = original
    .replace(/^#+[^\S\n]*\d+(?:\.\d+)*\.[^\S\n]*$/gm, '')
    .replace(/\n{3,}/g, '\n\n');
  let counter = 0;
  const renumbered = cleaned.replace(
    /^(#+[^\S\n]*)(\d+(?:\.\d+)*)(\.[^\S\n]+.+)$/gm,
    (_match, prefix, _number, suffix) => `${prefix}${++counter}${suffix}`,
  );
  if (renumbered !== original) {
    $setEditorValue(renumbered, transformers);
  }
}

export function EditorReadyPlugin({ onEditorReady, placeholderImages }: EditorReadyPluginProps) {
  const [editor] = useLexicalComposerContext();
  const transformers = createTransformers(editor);
  const onEditorReadyRef = useRef(onEditorReady);
  const placeholderImagesRef = useRef(placeholderImages);

  useEffect(() => {
    onEditorReadyRef.current = onEditorReady;
  }, [onEditorReady]);

  useEffect(() => {
    placeholderImagesRef.current = placeholderImages;
  }, [placeholderImages]);

  useEffect(() => {
    const callback = onEditorReadyRef.current;
    if (!callback) return;

    const getContent = (): string => {
      let content = '';
      editor.getEditorState().read(() => {
        const markdown = $getEditorValue(transformers);
        content = replaceImagesWithPlaceholders(markdown, placeholderImagesRef.current);
      });
      return stripNoteSuggestionTags(content);
    };

    const getRawContent = (): string => {
      let content = '';
      editor.getEditorState().read(() => {
        const markdown = $getEditorValue(transformers);
        content = replaceImagesWithPlaceholders(markdown, placeholderImagesRef.current);
      });
      return content;
    };

    const setContent = (markdown: string, onComplete?: () => void): void => {
      const restoreScroll = captureScrollPosition(editor);

      editor.update(
        () => {
          $setEditorValue(markdown, transformers);
        },
        { tag: SKIP_SCROLL_INTO_VIEW_TAG },
      );

      restoreScroll(onComplete);
    };

    const applyInlineSuggestions = (): void => {
      const restoreScroll = captureScrollPosition(editor);
      editor.update(
        () => {
          $acceptSuggestions();
          $cleanOrphanHeadingsAndRenumber(transformers);
        },
        { tag: SKIP_SCROLL_INTO_VIEW_TAG },
      );
      restoreScroll();
    };

    const discardSuggestions = (): void => {
      const restoreScroll = captureScrollPosition(editor);
      editor.update(
        () => {
          $discardSuggestions();

          const htmlOriginIndices = $collectHtmlOriginTableIndices();

          const markdown = $getEditorValue(transformers);
          $setEditorValue(markdown, transformers);

          $restoreHtmlOriginMarkers(htmlOriginIndices);
        },
        { tag: SKIP_SCROLL_INTO_VIEW_TAG },
      );
      restoreScroll();
    };

    const setEditable = (editable: boolean): void => {
      editor.setEditable(editable);
    };

    callback({
      editor,
      getContent,
      getRawContent,
      setContent,
      applyInlineSuggestions,
      discardSuggestions,
      setEditable,
    });
  }, [editor, transformers]);

  return null;
}
